import React, { useEffect, useState } from "react";
import { Download, File, RefreshCw, X } from "lucide-react";
import { request } from "../../api/client";

export default function ChatAttachments({ attachments = [], onRefresh }) {
  return <div>{attachments.map((attachment) => <Attachment key={attachment.id} attachment={attachment} onRefresh={onRefresh} />)}</div>;
}

function Attachment({ attachment, onRefresh }) {
  const [thumbnail, setThumbnail] = useState("");
  const [download, setDownload] = useState("");
  const [original, setOriginal] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const base = `/bots/${encodeURIComponent(attachment.bot_id)}/files`;

  useEffect(() => {
    if (!attachment.thumbnail_id || attachment.status !== "available") return;
    let active = true;
    const load = () => request(`${base}/${attachment.thumbnail_id}/access`, { params: { preview: true }, skipCache: true })
      .then((response) => { if (active) { setThumbnail(response.data.url); setError(""); } })
      .catch(() => { if (active) setError("Preview unavailable"); });
    load();
    const timer = setInterval(load, 240000);
    return () => { active = false; clearInterval(timer); };
  }, [base, attachment.thumbnail_id, attachment.status, refresh]);

  useEffect(() => {
    if (!download) return;
    const timer = setTimeout(() => setDownload(""), 240000);
    return () => clearTimeout(timer);
  }, [download]);

  const getOriginal = async (view = false) => {
    setBusy(true);
    setError("");
    try {
      const response = await request(`${base}/${attachment.file_id}/access`, { params: { inline: view }, skipCache: true });
      if (view) setOriginal(response.data.url);
      else {
        setDownload(response.data.url);
        const link = document.createElement("a");
        link.href = response.data.url;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch { setError("Download unavailable"); }
    finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    setError("");
    try {
      await request(`${base}/${attachment.file_id}/retry`, { method: "POST" });
      await onRefresh?.();
    } catch { setError("Attachment retry failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 min-w-0 space-y-2 rounded border border-current p-2" style={{ maxWidth: 320 }}>
      {thumbnail && !error ? <button type="button" aria-label={`View original ${attachment.name}`} title="View original" disabled={busy}
        onClick={() => getOriginal(true)} className="block w-full cursor-zoom-in">
        <img src={thumbnail} alt={`Preview of ${attachment.name}`} loading="lazy"
        onError={() => setError("Preview unavailable")}
        className="block max-h-60 w-full rounded object-contain" style={{ aspectRatio: "4 / 3" }} /></button>
        : <File size={28} aria-label="File attachment" />}
      <div className="break-all text-sm font-semibold">{attachment.name}</div>
      <div className="text-xs">{attachment.mime_type}{attachment.size_bytes != null ? ` · ${(attachment.size_bytes / 1024).toFixed(1)} KB` : ""}</div>
      {attachment.status === "available" ? (
        download ? <a href={download} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline"><Download size={14} />Download original</a>
          : <button type="button" disabled={busy} onClick={() => getOriginal()} className="inline-flex items-center gap-1 underline"><Download size={14} />{busy ? "Loading…" : "Download original"}</button>
      ) : <div role="status" className="text-xs">{attachment.status === "failed" ? "Attachment could not be saved" : "Processing attachment…"}</div>}
      {attachment.status === "failed" && attachment.can_retry && <button type="button" disabled={busy} onClick={retry} className="inline-flex items-center gap-1 underline"><RefreshCw size={14} />Retry saving</button>}
      {error && <div role="status" className="text-xs">{error} <button type="button" title="Retry preview" aria-label="Retry preview" onClick={() => { setError(""); setRefresh((value) => value + 1); }}><RefreshCw size={12} /></button></div>}
      {original && <div role="dialog" aria-modal="true" aria-label={`Original ${attachment.name}`} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 text-white">
        <button type="button" onClick={() => setOriginal("")} aria-label="Close original preview" title="Close" className="absolute right-4 top-4 p-2"><X size={24} /></button>
        {attachment.mime_type.startsWith("image/") ? <img src={original} alt={`Original ${attachment.name}`} className="max-h-[85vh] max-w-full object-contain" />
          : <iframe src={original} title={`Original ${attachment.name}`} className="h-[85vh] w-full max-w-4xl rounded bg-white" sandbox="allow-scripts allow-same-origin" />}
      </div>}
    </div>
  );
}